import { clearLocalStorageValue } from "util/localStorageHelper";
import axios, { AxiosError } from "axios";
import { DateTime } from "luxon";
import { useSnackbar } from "notistack";
import { useIntl } from "react-intl";
import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from "@tanstack/react-query";

import { context_root } from "app.config";
import { Api } from "lib/Api";
import { apiErrorMessage } from "lib/apiErrorMessage";
import { Pageable } from "types/common/Pageable";

/**
 * The react query key the custom texts are cached under.
 *
 * Every read hook in this file keys off this value, so invalidating it invalidates all of them
 * (react query matches query keys by prefix).
 */
export const KEY = "customTexts";

/** The localStorage key the fetched custom texts are cached under */
const STORAGE_KEY = "custom_texts";

/** The localStorage key holding the expiry of the {@link STORAGE_KEY} cache */
const STORAGE_EXPIRY_KEY = "custom_texts_expiry";

const api = new Api<CustomTextUpdate>("custom_texts", "custom_text");

const DEFAULT_CUSTOM_TEXTS: CustomTextResult = {
  erg: "ERG",
  program: "Program",
  structure: "Structure",
  outcome: "Outcome",
  badge: "Badge",
  segment: "Segment",
  gamification: "Gamification",
  gamify: "Gamify",
  member_preference: "Member Preference",
  parent: "Parent",
  sub_erg: "Sub ERG",
  privacy_statement: "Privacy Statement",
  region: "Region",
  browser_title: "CommunityCliQ",
  employee_id: "Employee ID",
  plural: {
    erg: "ERGs",
    program: "Programs",
    structure: "Structures",
    outcome: "Outcomes",
    badge: "Badges",
    segment: "Segments",
    gamification: "Gamification",
    gamify: "Gamify",
    member_preference: "Member Preferences",
    parent: "Parents",
    sub_erg: "Sub ERGs",
    privacy_statement: "Privacy Statements",
    region: "Regions",
    browser_title: "CommunityCliQ",
    employee_id: "Employee IDs",
  },
};

interface CustomText {
  erg: string;
  program: string;
  structure: string;
  outcome: string;
  badge: string;
  segment: string;
  gamification: string;
  gamify: string;
  member_preference: string;
  parent: string;
  sub_erg: string;
  privacy_statement: string;
  region: string;
  browser_title: string;
  employee_id: string;
}

export interface CustomTextResult extends CustomText {
  /**
   * The primary key of the enterprise's custom texts row.
   *
   * Optional because payloads written to localStorage by older versions of the frontend do not
   * contain it, and because the hardcoded defaults are not a persisted row. The API always returns
   * it, and it is required in order to update the row - see
   * {@link useUpdateCustomTextsMutation}.
   */
  id?: number;

  plural: CustomText;
}

/**
 * The custom text attributes that the update endpoint accepts.
 *
 * This is deliberately narrower than {@link CustomText}: these eight are the only attributes the
 * endpoint permits. The other keys on {@link CustomText} (`badge`, `segment`, `gamify`,
 * `member_preference`, `privacy_statement`, `region`, `browser_title`) are silently dropped, so
 * they must not be sent.
 *
 * All eight are validated as present, non blank and at most 191 characters, so every field is a
 * required, non empty string.
 */
export interface CustomTextUpdate {
  erg: string;
  program: string;
  structure: string;
  outcome: string;
  gamification: string;
  parent: string;
  sub_erg: string;
  employee_id: string;
}

/** The {@link CustomTextUpdate} attributes plus the id of the row being updated */
export interface CustomTextUpdateVariables extends CustomTextUpdate {
  id: number;
}

export interface OldCustomText extends CustomText {
  erg_p: string;
  program_p: string;
  structure_p: string;
  outcome_p: string;
  badge_p: string;
  segment_p: string;
  gamification_p: string;
  gamify_p: string;
  member_preference_p: string;
  parent_p: string;
  sub_erg_p: string;
  privacy_statement_p: string;
  region_p: string;
  browser_title_p: string;
  employee_id: string;
}

async function refetch(): Promise<CustomTextResult> {
  return axios
    .get<Pageable<CustomTextResult>>(`${context_root}/custom_texts`)
    .then((resp) => resp.data)
    .then((data) => data.page.items[0] as CustomTextResult | undefined)
    .then((data) => {
      if (data) {
        localStorage.setItem(
          STORAGE_EXPIRY_KEY,
          DateTime.now().plus({ hours: 4 }).toISO(),
        );
        localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      }
      return data ?? DEFAULT_CUSTOM_TEXTS;
    });
}

/**
 * If the data is stored and not expired, return it.
 * If the data is stored but is expired, return it, but trigger a background refetch
 * If the data is not stored, refetch and return the data
 */
async function fetchCustomTexts(): Promise<CustomTextResult> {
  const expiry = localStorage.getItem(STORAGE_EXPIRY_KEY);
  const stored = localStorage.getItem(STORAGE_KEY);

  if (stored) {
    if (expiry && DateTime.fromISO(expiry) > DateTime.now()) {
      return JSON.parse(stored) as CustomTextResult;
    }

    void refetch();
    return JSON.parse(stored) as CustomTextResult;
  }

  return refetch();
}

/**
 * Drops the localStorage copy of the custom texts.
 *
 * {@link fetchCustomTexts} answers out of localStorage whenever an unexpired copy is stored, so
 * invalidating the react query cache on its own is not enough to pick up a change - the stored copy
 * has to go first, otherwise the refetch just reads the stale value straight back out again.
 */
function clearStoredCustomTexts(): void {
  clearLocalStorageValue(STORAGE_KEY);
  clearLocalStorageValue(STORAGE_EXPIRY_KEY);
}

export function useCustomTexts(): UseQueryResult<CustomTextResult, AxiosError> {
  return useQuery({
    queryKey: [KEY],
    queryFn: fetchCustomTexts,
  });
}

export function useOldCustomTexts(): UseQueryResult<OldCustomText, AxiosError> {
  return useQuery({
    queryKey: [KEY, "mapped"],
    queryFn: () =>
      fetchCustomTexts().then((customTexts) => ({
        ...customTexts,
        erg_p: customTexts.plural.erg,
        program_p: customTexts.plural.program,
        structure_p: customTexts.plural.structure,
        outcome_p: customTexts.plural.outcome,
        badge_p: customTexts.plural.badge,
        segment_p: customTexts.plural.segment,
        gamification_p: customTexts.plural.gamification,
        gamify_p: customTexts.plural.gamify,
        member_preference_p: customTexts.plural.member_preference,
        parent_p: customTexts.plural.parent,
        sub_erg_p: customTexts.plural.sub_erg,
        privacy_statement_p: customTexts.plural.privacy_statement,
        region_p: customTexts.plural.region,
        browser_title_p: customTexts.plural.browser_title,
        employee_id: customTexts.plural.employee_id,
      })),
  });
}

/**
 * Use a custom text, optionally pluralizing the word
 *
 * @param key The custom text key
 * @param pluralize  Whether to pluralize the word or not
 * @return The custom string, falling back to its default while the query is pending or failed
 */
export function useCustomText(
  key: keyof CustomText,
  pluralize?: boolean,
): string;

/**
 * Use a custom text for multiple keys, optionally pluralizing all words
 *
 * @param key The custom text keys
 * @param pluralize  Whether to pluralize the word or not
 * @return The custom strings, falling back to their defaults while the query is pending or failed
 */
export function useCustomText(
  key: (keyof CustomText)[],
  pluralize?: boolean,
): string[];

/**
 * Use a custom text, optionally pluralizing the word
 *
 * @param key The custom text key
 * @param pluralize  Whether to pluralize the word or not
 * @return The localized string, falling back to its default while the query is pending or failed
 */
export function useCustomText(
  key: keyof CustomText | (keyof CustomText)[],
  pluralize?: boolean,
): string | string[] {
  const { data } = useCustomTexts();
  // Never an empty string. These values are interpolated into user-facing labels - "Find
  // {ergs}", "Leave {ergText}" - and an empty one renders a link with no visible text that is
  // still laid out and still clickable, which is indistinguishable from a broken page. The
  // hardcoded defaults existed already but were unreachable here: `refetch` only falls back to
  // them when the request *succeeds* and returns no row, so a failed or still-pending
  // `/custom_texts` call left every label blank.
  const texts = data ?? DEFAULT_CUSTOM_TEXTS;
  if (key instanceof Array) {
    return key.map((k) => resolve(texts, k, pluralize));
  }
  return resolve(texts, key, pluralize);
}

/**
 * One custom text, falling back to its default when the configured value is missing or blank.
 *
 * Blank as well as absent, because the two are the same thing to a reader. The update endpoint
 * validates every attribute as non blank, but it only governs the eight in
 * {@link CustomTextUpdate} - and a row written by anything else, or a payload left in
 * localStorage by an older frontend, is under no such guarantee.
 */
function resolve(
  texts: CustomTextResult,
  key: keyof CustomText,
  pluralize?: boolean,
): string {
  // Read through Partial, which the interface does not admit but the data does:
  // `fetchCustomTexts` casts a `JSON.parse` of localStorage, so a payload written by an older
  // frontend can be missing a key the type promises is there.
  const source: Partial<CustomText> = pluralize ? texts.plural : texts;
  const value = source[key];
  if (value?.trim()) {
    return value;
  }
  return pluralize
    ? DEFAULT_CUSTOM_TEXTS.plural[key]
    : DEFAULT_CUSTOM_TEXTS[key];
}

/**
 * Update the enterprise's custom texts.
 *
 * `PUT /api/v1/custom_texts/:id` with the attributes wrapped in a `custom_text` root. The endpoint
 * requires the `MANAGE_ENTERPRISE_SETTINGS` authority.
 *
 * On success every custom texts query is invalidated, and the localStorage copy that backs them is
 * dropped, so that all consumers (`useCustomTexts`, `useOldCustomTexts`, `useCustomText`,
 * `CustomTextFormattedMessage`, ...) re-render with the new labels rather than showing stale ones
 * until the next reload.
 *
 * The `plural` values are computed server side, which is why the caches are refreshed from the
 * server rather than being patched locally.
 *
 * @param options Additional react query mutation options
 */
export function useUpdateCustomTextsMutation(
  options?: UseMutationOptions<
    CustomTextUpdate,
    AxiosError<{ message?: string }>,
    CustomTextUpdateVariables
  >,
): UseMutationResult<
  CustomTextUpdate,
  AxiosError<{ message?: string }>,
  CustomTextUpdateVariables
> {
  const { enqueueSnackbar } = useSnackbar();
  const intl = useIntl();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...attributes }) =>
      api.update(id, attributes).then((resp) => resp.data),
    ...options,
    onSuccess: async (...args) => {
      clearStoredCustomTexts();
      await queryClient.invalidateQueries({ queryKey: [KEY] });
      enqueueSnackbar(
        intl.formatMessage({
          defaultMessage: "Custom labels were updated successfully",

          description:
            "Snackbar success message when the custom labels are updated",
        }),
        { variant: "success" },
      );
      return options?.onSuccess?.(...args);
    },
    onError: (...args) => {
      const [error] = args;
      enqueueSnackbar(
        apiErrorMessage(
          error,
          intl.formatMessage({
            defaultMessage: "Failed to update custom labels",

            description:
              "Snackbar error message when updating the custom labels fails",
          }),
        ),
        { variant: "error" },
      );
      return options?.onError?.(...args);
    },
  });
}
